import { format } from 'date-fns';
import type { TopicVersion, TopicVersionSnapshot } from '../../types/graphql';

/**
 * An article a write key sent isn't in tinycms yet, so it has no History screen.
 * Write keys need no approval, so what one did is listed on the article instead
 * (backlog 7654d9ef; apiserver wpIngest.js). Once it's brought in, these versions
 * are the start of its History.
 */

const FIELDS: Array<[keyof TopicVersionSnapshot, string]> = [
  ['title', 'title'],
  ['summary', 'summary'],
  ['bodyText', 'text'],
  ['category', 'category'],
  ['keywords', 'keywords'],
  ['publishedDate', 'date'],
  ['mediaItems', 'images'],
  ['customFields', 'custom fields'],
  ['publishStatus', 'status'],
];

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function listed(names: string[]): string {
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function what(snap: TopicVersionSnapshot, prev?: TopicVersionSnapshot): string {
  switch (snap.sentBy?.action) {
    case 'sent':
      return 'Sent in';
    case 'off':
      return 'Taken off the site';
    case 'deleted':
      return 'Deleted';
    case 'changed': {
      const changed = FIELDS.filter(([key]) => prev && !same(snap[key], prev[key])).map(
        ([, name]) => name
      );
      return changed.length ? `Changed the ${listed(changed)}` : 'Changed';
    }
    default:
      return 'As it was before';
  }
}

export interface WriteKeyRow {
  v: number;
  what: string;
  who: string;
  when: string;
}

/** Newest first, or nothing when no write key has touched this article. */
export function writeKeyRows(versions: TopicVersion[]): WriteKeyRow[] {
  if (!versions.some((v) => v.snapshot?.sentBy)) return [];
  const oldestFirst = [...versions].sort((a, b) => a.versionNumber - b.versionNumber);
  return oldestFirst
    .map((v, i) => ({
      v: v.versionNumber,
      what: what(v.snapshot || {}, oldestFirst[i - 1]?.snapshot),
      who: v.snapshot?.sentBy ? `${v.snapshot.sentBy.name} · write key` : '',
      when: format(new Date(v.createdAt), 'MMM d, h:mm a'),
    }))
    .reverse();
}
